import { useCallback, useEffect, useRef, useState } from "react";
import { t } from "ttag";
import * as Yup from "yup";

import { useSendMfaSmsMutation, useVerifyMfaMutation } from "metabase/api";
import {
  Form,
  FormErrorMessage,
  FormProvider,
  FormSubmitButton,
  FormTextInput,
} from "metabase/forms";
import type { AuthChallengeFormProps } from "metabase/plugins/oss/multi-factor-auth";
import { useDispatch } from "metabase/redux";
import { completeLogin } from "metabase/redux/auth";
import { Anchor, Button, Stack, Text } from "metabase/ui";
import * as Errors from "metabase/utils/errors";

/** Seconds before "Resend" becomes available. The backend throttles the same window. */
const RESEND_COOLDOWN_SECONDS = 30;

const CODE_SCHEMA = Yup.object().shape({
  code: Yup.string().required(Errors.required),
});

/**
 * 99digital: second step of a password login for users who have an SMS 2FA number saved.
 *
 * The code is generated and checked by the SMS provider; this form only relays it. On success the
 * backend creates the session (POST /api/session/mfa/verify sets the cookie), so we finish with
 * the same `completeLogin` thunk a password-only login uses.
 */
export const SmsChallengeForm = ({
  challengeToken,
  remember,
  onCancel,
}: AuthChallengeFormProps): JSX.Element => {
  const dispatch = useDispatch();
  const [verifyMfa] = useVerifyMfaMutation();
  const [sendMfaSms] = useSendMfaSmsMutation();

  const [secondsLeft, setSecondsLeft] = useState(RESEND_COOLDOWN_SECONDS);
  const [sendError, setSendError] = useState<string | null>(null);
  // StrictMode double-invokes effects in dev; without this the initial send fires twice and the
  // second one trips the backend's 30s throttle.
  const hasSentRef = useRef(false);

  const sendCode = useCallback(async () => {
    setSendError(null);
    try {
      await sendMfaSms({ challenge_token: challengeToken }).unwrap();
      setSecondsLeft(RESEND_COOLDOWN_SECONDS);
    } catch {
      setSendError(t`We couldn't send the code. Please try again shortly.`);
    }
  }, [challengeToken, sendMfaSms]);

  useEffect(() => {
    if (!hasSentRef.current) {
      hasSentRef.current = true;
      sendCode();
    }
  }, [sendCode]);

  useEffect(() => {
    if (secondsLeft <= 0) {
      return;
    }
    const id = setTimeout(() => setSecondsLeft((n) => n - 1), 1000);
    return () => clearTimeout(id);
  }, [secondsLeft]);

  const handleSubmit = useCallback(
    async ({ code }: { code: string }) => {
      await verifyMfa({
        challenge_token: challengeToken,
        code,
        remember,
      }).unwrap();
      await dispatch(completeLogin()).unwrap();
    },
    [challengeToken, remember, verifyMfa, dispatch],
  );

  return (
    <FormProvider
      initialValues={{ code: "" }}
      validationSchema={CODE_SCHEMA}
      onSubmit={handleSubmit}
    >
      <Form>
        <Stack gap="1.25rem">
          <Text c="text-secondary">
            {t`We sent a verification code by SMS to the phone number on your account. Enter it below to finish signing in.`}
          </Text>

          <FormTextInput
            name="code"
            label={t`Verification code`}
            placeholder="123456"
            autoFocus
            autoComplete="one-time-code"
            inputMode="numeric"
          />

          <FormSubmitButton label={t`Verify`} variant="filled" w="100%" />
          <FormErrorMessage />

          {sendError && (
            <Text c="error" size="sm" ta="center">
              {sendError}
            </Text>
          )}

          <Text c="text-light" size="sm" ta="center">
            {secondsLeft > 0 ? (
              t`You can request a new code in ${secondsLeft}s`
            ) : (
              <Button variant="subtle" p={0} onClick={sendCode}>
                {t`Resend code`}
              </Button>
            )}
          </Text>

          <Text ta="center" size="sm">
            <Anchor component="button" type="button" onClick={onCancel}>
              {t`Back to sign in`}
            </Anchor>
          </Text>
        </Stack>
      </Form>
    </FormProvider>
  );
};
